import React, {
  forwardRef, useMemo, useRef, useState,
} from 'react';
import { useInView } from 'react-intersection-observer';

import { Chip } from '@tmap/mmm-style-guide/src/Chip';
import { Grid } from '@tmap/mmm-style-guide/src/Grid';
import { MoreHoriz, PushPin } from '@tmap/mmm-style-guide/src/Icon';
import { IconButton } from '@tmap/mmm-style-guide/src/IconButton';
import { styled } from '@tmap/mmm-style-guide/src/styled';

import { topics } from '@tmap/mmm-core/contributionTopics';

import 'swiper/css';

import ContributionCardMenu from './contributionCardMenu';

const StyledChip = styled(Chip)(({ theme }) => ({
  marginRight: theme.spacing(1),
  backgroundColor: theme.palette.primary.veryLight,
  color: theme.palette.primary.main,
  ':last-child': {
    textOverflow: 'ellipsis',
    overflowX: 'hidden',
    marginRight: 0,
    minWidth: '40px',
  },
}));

const TopicContainer = styled(Grid)(() => ({
  display: 'flex',
  flex: '1 2',
  overflowX: 'hidden',
}));

const PinnedIndicator = styled(PushPin)(({ theme }) => ({
  marginRight: theme.spacing(0.5),
  color: theme.palette.text.disabled,
}));

const CenteredGrid = styled(Grid)(() => ({
  display: 'flex',
  alignItems: 'center',
}));

const MenuIcon = styled(MoreHoriz)(() => ({
  margin: '-2px',
}));

const TopicChip = forwardRef((props, rootRef) => {
  const {
    id,
    label,
    contributionId,
  } = props;
  const { inView, ref, entry } = useInView({ root: rootRef.current });
  const [truncate, setTruncate] = useState(false);

  if (!truncate && inView && entry.intersectionRatio < 1) {
    setTruncate(true);
  }
  return (
    truncate
      ? null
      : (
        <StyledChip
          ref={ref}
          key={`topic-chip-${id}-${contributionId}`}
          variant='filled'
          size='small'
          label={label}
        />
      )
  );
});

function CardHeader(props) {
  const {
    contribution,
    moderationView = false,
    showMenuNearText = false,
    showTopics = true,
    showPinned = true,
  } = props;

  const {
    _id,
    topics: contributionTopics = [],
    hashtags = [],
  } = contribution;

  const topicContainerRef = useRef(null);

  const chipValues = useMemo(() => {
    const allChips = [];
    contributionTopics.forEach((topicId) => {
      const topic = topics[topicId]?.plural;
      if (topic) {
        allChips.push({
          id: topicId,
          label: topic,
        });
      }
    });
    allChips.push(...hashtags.map((hashtag) => ({
      id: hashtag,
      label: hashtag,
    })));
    return allChips;
  }, [contributionTopics, hashtags]);

  return (
    <Grid container alignItems='center' spacing={0.5}>
      <TopicContainer
        item
        ref={topicContainerRef}
      >
        {showTopics && chipValues.map(({ id, label }, i) => (
          <TopicChip
            id={id}
            label={label}
            contributionId={_id}
            key={`t-${i}`}
            ref={topicContainerRef}
          />
        ))}
      </TopicContainer>
      {showPinned && contribution.pinned && (
        <CenteredGrid item>
          <PinnedIndicator fontSize='small' />
        </CenteredGrid>
      )}
      {!showMenuNearText && !moderationView && (
        <Grid item>
          <ContributionCardMenu contribution={contribution}>
            {(handleOpenMenu) => (
              <IconButton size='small' onClick={handleOpenMenu} aria-label='post options menu'>
                <MenuIcon />
              </IconButton>
            )}
          </ContributionCardMenu>
        </Grid>
      )}
    </Grid>

  );
}

export default CardHeader;
